import { useState, useEffect } from "react";
import { Menu, X, ArrowUpRight } from "lucide-react";

const Navigation = () => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 20);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const scrollToSection = (id) => {
        const el = document.getElementById(id);
        if (el) {
            el.scrollIntoView({ behavior: "smooth" });
            setIsMenuOpen(false);
        }
    };

    const links = [
        { id: "solutions", label: "Solutions" },
        { id: "work", label: "Work" },
        { id: "impact", label: "Impact" },
        { id: "services", label: "Services" },
    ];

    return (
        <nav
            className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
                scrolled
                    ? "bg-ink-950/85 backdrop-blur-xl border-b border-white/5"
                    : "bg-transparent"
            }`}
            data-testid="main-nav"
        >
            <div className="max-w-7xl mx-auto px-6 lg:px-10">
                <div className="flex items-center justify-between h-20">
                    {/* Brand — real logo */}
                    <button
                        onClick={() => scrollToSection("home")}
                        className="flex items-center gap-3 group"
                        data-testid="nav-logo"
                        aria-label="APS Data Technologies"
                    >
                        <img
                            src="/logo.png"
                            alt="APS Data Technologies"
                            className="h-14 w-auto object-contain transition-transform group-hover:scale-105"
                            style={{ maxWidth: "200px" }}
                        />
                    </button>

                    {/* Desktop Nav */}
                    <div className="hidden lg:flex items-center gap-1">
                        {links.map((link) => (
                            <button
                                key={link.id}
                                onClick={() => scrollToSection(link.id)}
                                className="px-4 py-2 text-[14px] text-white/70 hover:text-white transition-colors rounded-full hover:bg-white/5"
                                data-testid={`nav-${link.id}`}
                            >
                                {link.label}
                            </button>
                        ))}
                    </div>

                    {/* CTA */}
                    <div className="hidden lg:flex items-center gap-3">
                        <button
                            onClick={() => scrollToSection("contact")}
                            className="group flex items-center gap-2 px-5 py-2.5 rounded-full bg-accent-brand text-ink-950 text-[14px] font-semibold hover:bg-white transition-all duration-300"
                            data-testid="nav-cta"
                        >
                            Start a project
                            <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                        </button>
                    </div>

                    {/* Mobile toggle */}
                    <button
                        className="lg:hidden text-white p-2"
                        onClick={() => setIsMenuOpen(!isMenuOpen)}
                        data-testid="mobile-menu-toggle"
                    >
                        {isMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                    </button>
                </div>
            </div>

            {/* Mobile menu */}
            {isMenuOpen && (
                <div className="lg:hidden bg-ink-950/95 backdrop-blur-xl border-t border-white/5">
                    <div className="px-6 py-4 space-y-1">
                        {links.map((link) => (
                            <button
                                key={link.id}
                                onClick={() => scrollToSection(link.id)}
                                className="block w-full text-left px-4 py-3 text-white/80 hover:text-accent-brand hover:bg-white/5 rounded-lg transition"
                            >
                                {link.label}
                            </button>
                        ))}
                        <button
                            onClick={() => scrollToSection("contact")}
                            className="block w-full text-center mt-3 px-4 py-3 rounded-full bg-accent-brand text-ink-950 font-semibold"
                        >
                            Start a project →
                        </button>
                    </div>
                </div>
            )}
        </nav>
    );
};

export default Navigation;
